import { automationApi, Tag } from 'api/automationApi'

import type { RootState } from '../../redux/store'
import type * as Api from './model/types'

import { updateReactions } from './utils/optimistic'
import { fetchAttachments } from './utils/query'
import { saveConversations, saveMessages } from './utils/update'

export const api = automationApi.injectEndpoints({
	endpoints: create => ({
		archive: create.mutation<void, Api.UpdateConversation>({
			invalidatesTags: [Tag.Conversation],
			query: ({ id, mark }) => ({
				method: 'PUT',
				url: `conversations/${id}/${mark ? 'archive' : 'unarchive'}`
			})
		}),

		attachment: create.query<string, Api.AttachmentProps>({
			queryFn: fetchAttachments
		}),

		conversations: create.query<Api.ConversationResponse, Api.GetProps>({
			providesTags: [Tag.Conversation],
			query: ({ filter, search }) => {
				const type = search ? 'search' : filter

				return {
					params: {
						keywords: search || undefined
					},
					url: `mailboxes/${type}/conversations/recent`
				}
			},

			transformResponse: saveConversations
		}),

		conversationsNext: create.query<Api.ConversationResponse, Api.NextProps>({
			providesTags: [Tag.Conversation],
			query: ({ filter, nextCursor, search }) => {
				const type = search ? 'search' : filter

				return {
					params: {
						keywords: search || undefined,
						nextCursor
					},
					url: `mailboxes/${type}/conversations/next`
				}
			},

			transformResponse: saveConversations
		}),

		delete: create.mutation<void, string>({
			invalidatesTags: [Tag.Conversation],
			query: id => ({
				method: 'DELETE',
				url: `conversations/${id}`
			})
		}),

		deleteMessage: create.mutation<void, Api.DeleteMessageBody>({
			invalidatesTags: [Tag.Messages],
			query: ({ conversation, id }) => ({
				method: 'DELETE',
				url: `conversations/${conversation}/events/${id}`
			})
		}),

		editMessage: create.mutation<void, Api.EditMessageBody>({
			invalidatesTags: [Tag.Messages],
			query: ({ conversation, id, message }) => ({
				data: message,
				headers: {
					/**
					 * ! Don't delete
					 * RTK Query sends plain string as FormData if Content-Type is not set
					 *  */
					'Content-Type': 'application/json'
				},
				method: 'PUT',
				url: `conversations/${conversation}/events/${id}`
			})
		}),

		markRead: create.mutation<void, Api.UpdateConversation>({
			invalidatesTags: [Tag.Conversation, Tag.UnreadStats],
			query: ({ id, mark }) => ({
				method: 'PUT',
				url: `conversations/${id}/${mark ? 'read' : 'unread'}`
			})
		}),

		messages: create.query<Api.EventsResponse, string>({
			providesTags: [Tag.Messages],
			query: conversation => ({
				url: `conversations/${conversation}/events/recent`
			}),
			transformResponse: saveMessages
		}),

		messagesPrev: create.query<
			Api.EventsResponse,
			{ id: string; prevCursor: string }
		>({
			providesTags: [Tag.Messages],
			query: ({ id, prevCursor }) => ({
				params: {
					prevCursor
				},
				url: `conversations/${id}/events/next`
			}),
			transformResponse: saveMessages
		}),

		participants: create.query<Api.Participant[], string>({
			query: conversation => ({
				url: `conversations/${conversation}/participants`
			})
		}),

		reaction: create.mutation<void, Api.ReactionBody>({
			invalidatesTags: [Tag.Messages],
			onQueryStarted: async (props, { dispatch, getState, queryFulfilled }) => {
				const state = getState() as RootState

				const cancel = updateReactions(props, { dispatch, state })

				try {
					await queryFulfilled
				} catch {
					cancel()
				}
			},
			query: ({ conversation, emoji, mark, message }) => ({
				method: mark ? 'POST' : 'DELETE',
				url: `conversations/${conversation}/events/${message}/reactions/${emoji}`
			})
		}),

		sendMessage: create.mutation<void, Api.MewMessageBody>({
			invalidatesTags: [Tag.Messages],
			query: ({ conversation, message }) => ({
				data: { attachments: [], message },
				method: 'POST',
				url: `conversations/${conversation}/events`
			})
		}),

		setFocus: create.mutation<void, string>({
			invalidatesTags: [Tag.Conversation],
			query: id => ({
				method: 'PUT',
				url: `conversations/${id}/focused`
			})
		}),

		setOther: create.mutation<void, string>({
			invalidatesTags: [Tag.Conversation],
			query: id => ({
				method: 'PUT',
				url: `conversations/${id}/other`
			})
		}),

		settings: create.query<{ focusedInboxEnabled: boolean }, void>({
			query: () => ({
				url: 'settings'
			})
		}),
		star: create.mutation<void, Api.UpdateConversation>({
			query: ({ id, mark }) => ({
				method: 'PUT',
				url: `conversations/${id}/${mark ? 'star' : 'unstar'}`
			})
		}),

		sync: create.mutation<string, any>({
			query: id => ({
				method: 'POST',
				url: `conversations/${id}/sync`
			})
		}),

		unread: create.query<Api.UnreadStats, void>({
			providesTags: [Tag.UnreadStats],
			query: () => ({
				url: 'messaging/unread-conversations-counters'
			})
		})
	}),
	overrideExisting: true
})
